import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { RouterFacade } from '@rx-applications/shared/data-access';
import { AuthFacade } from '../../facade/auth.facade';
import { AuthBackend } from '../../../backend/auth.backend';
import {
  loginActions,
  resendCodeActions,
  saveTokenDetails,
  setupMFAActions,
  smsChallengeActions,
} from '../../actions/auth.actions';
import { Credentials, LoginResponse, MfaType } from '../../../models';
import { AuthRoutes } from '../../../constants';

@Injectable()
export class MfaEffects {
  constructor(
    private actions$: Actions,
    private authBackend: AuthBackend,
    private router: Router,
    private routerFacade: RouterFacade,
    private authFacade: AuthFacade
  ) {}

  smsChallenge$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(smsChallengeActions.request),
        withLatestFrom(this.routerFacade.queryParams$),
        tap(([, queryParams]) => this.router.navigate([AuthRoutes.Verify], { queryParams }))
      ),
    { dispatch: false }
  );

  resendCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resendCodeActions.resend),
      withLatestFrom(this.authFacade.credentials$),
      map(([, credentials]) => credentials),
      switchMap((credentials: Credentials) =>
        this.authBackend.login(credentials, undefined).pipe(
          map((loginResponse: LoginResponse) => resendCodeActions.succeed({ loginResponse })),
          catchError((error) => {
            return of(resendCodeActions.failed({ error }));
          })
        )
      )
    )
  );

  smsChallengeConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smsChallengeActions.confirm),
      withLatestFrom(this.authFacade.sessionId$, this.authFacade.credentials$, ({ code }, sessionId, { username }) => ({
        username,
        sessionId,
        code,
      })),
      switchMap(({ username, sessionId, code }) =>
        this.authBackend.confirmCode({ email: username, sessionId: sessionId, mfaCode: code }).pipe(
          map((loginResponse: LoginResponse) => smsChallengeActions.succeed({ loginResponse })),
          catchError((error) => of(smsChallengeActions.failed({ error })))
        )
      )
    )
  );

  smsChallengeSucceed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(smsChallengeActions.succeed),
      map(({ loginResponse }) => saveTokenDetails({ response: loginResponse }))
    )
  );

  setupMFAChallenge$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setupMFAActions.request),
        withLatestFrom(this.routerFacade.queryParams$),
        tap(([, queryParams]) => this.router.navigate([AuthRoutes.SetupMFA], { queryParams }))
      ),
    { dispatch: false }
  );

  setupMFAConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setupMFAActions.confirm),
      withLatestFrom(
        this.authFacade.sessionId$,
        this.authFacade.credentials$,
        ({ phoneNumber }, sessionId, { username }) => ({
          username,
          sessionId,
          phoneNumber,
        })
      ),
      switchMap(({ username, sessionId, phoneNumber }) =>
        this.authBackend
          .setupMfa({ email: username, sessionId: sessionId, mfaSmsPhoneNumber: phoneNumber, mfaType: MfaType.SMS })
          .pipe(
            map(() => setupMFAActions.succeed()),
            catchError((error) => of(setupMFAActions.failed({ error })))
          )
      )
    )
  );

  setupMFASucceed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setupMFAActions.succeed),
      withLatestFrom(this.authFacade.credentials$),
      map(([, credentials]) => loginActions.login({ credentials }))
    )
  );
}
